/**
 * Container Component Definition
 *
 * A flexible wrapper div that can hold any content components.
 * Supports nested containers and provides full styling options
 * including flex layout for arranging children.
 */

import { getGapSize } from '../../renderer/StyleResolver';

export const containerComponent = {
  type: 'container',
  label: 'Container',
  icon: 'Box',
  description: 'Flexible wrapper for grouping content',
  category: 'layout',

  // Container options
  canHaveChildren: true,

  resolveStyles(props) {
    const displayMode = props.display || 'flex';
    const isFlex = displayMode === 'flex';
    const gap = getGapSize(props.gap);

    return {
      display: displayMode,
      ...(isFlex ? {
        flexDirection: props.flexDirection || 'column',
        gap,
        alignItems: props.alignItems || 'stretch',
        justifyContent: props.justifyContent || 'flex-start',
        flexWrap: props.flexWrap || 'nowrap',
      } : displayMode === 'grid' ? { gap } : {}),
      position: props.position || 'static',
    };
  },

  defaultProps: {
    // Layout
    display: 'flex',
    flexDirection: 'column',
    gap: 'md',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',

    // Size
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },

    // Spacing
    padding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Appearance
    backgroundColor: 'transparent',
    opacity: 100,

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Advanced
    overflow: 'visible',
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Layout category
    {
      key: 'display',
      type: 'iconToggle',
      label: 'Display',
      category: 'Layout',
      default: 'flex',
      options: [
        { value: 'block', label: 'Block', icon: 'Square' },
        { value: 'flex', label: 'Flex', icon: 'Rows3' },
        { value: 'none', label: 'None', icon: 'EyeOff' },
      ],
    },
    {
      key: 'flexDirection',
      type: 'iconToggle',
      label: 'Direction',
      category: 'Layout',
      default: 'column',
      options: [
        { value: 'column', label: 'Vertical', icon: 'ArrowDown' },
        { value: 'row', label: 'Horizontal', icon: 'ArrowRight' },
        { value: 'column-reverse', label: 'Vertical Reverse', icon: 'ArrowUp' },
        { value: 'row-reverse', label: 'Horizontal Reverse', icon: 'ArrowLeft' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },
    {
      key: 'gap',
      type: 'select',
      label: 'Gap',
      category: 'Layout',
      default: 'md',
      options: [
        { value: 'none', label: 'None' },
        { value: 'sm', label: 'Small (8px)' },
        { value: 'md', label: 'Medium (16px)' },
        { value: 'lg', label: 'Large (24px)' },
        { value: 'xl', label: 'Extra Large (32px)' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },
    {
      key: 'alignItems',
      type: 'iconToggle',
      label: 'Align',
      category: 'Layout',
      default: 'stretch',
      options: [
        { value: 'flex-start', label: 'Start', icon: 'AlignStartVertical' },
        { value: 'center', label: 'Center', icon: 'AlignCenterVertical' },
        { value: 'flex-end', label: 'End', icon: 'AlignEndVertical' },
        { value: 'stretch', label: 'Stretch', icon: 'AlignVerticalSpaceAround' },
        { value: 'baseline', label: 'Baseline', icon: 'Baseline' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },
    {
      key: 'justifyContent',
      type: 'iconToggle',
      label: 'Justify',
      category: 'Layout',
      default: 'flex-start',
      options: [
        { value: 'flex-start', label: 'Start', icon: 'AlignHorizontalJustifyStart' },
        { value: 'center', label: 'Center', icon: 'AlignHorizontalJustifyCenter' },
        { value: 'flex-end', label: 'End', icon: 'AlignHorizontalJustifyEnd' },
        { value: 'space-between', label: 'Between', icon: 'AlignHorizontalSpaceBetween' },
        { value: 'space-around', label: 'Around', icon: 'AlignHorizontalSpaceAround' },
        { value: 'space-evenly', label: 'Evenly', icon: 'AlignHorizontalDistributeCenter' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },
    {
      key: 'flexWrap',
      type: 'iconToggle',
      label: 'Wrap',
      category: 'Layout',
      default: 'nowrap',
      options: [
        { value: 'nowrap', label: 'No Wrap', icon: 'WrapText' },
        { value: 'wrap', label: 'Wrap', icon: 'TextCursorInput' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },

    // Size category
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Appearance category
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Appearance',
      default: 'transparent',
      allowGradient: true,
    },
    {
      key: 'opacity',
      type: 'number',
      label: 'Opacity (%)',
      category: 'Appearance',
      min: 0,
      max: 100,
      default: 100,
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Advanced category
    {
      key: 'overflow',
      type: 'select',
      label: 'Overflow',
      category: 'Advanced',
      default: 'visible',
      options: [
        { value: 'visible', label: 'Visible' },
        { value: 'hidden', label: 'Hidden' },
        { value: 'scroll', label: 'Scroll' },
        { value: 'auto', label: 'Auto' },
        { value: 'clip', label: 'Clip' },
      ],
    },
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    // Custom CSS
    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  render: null,
};

export default containerComponent;
